/**
 * Agent Subfolder Expansion - Loads children for SQL Server Agent subfolders
 * (Operators, Alerts, etc.) when expanded in the Object Explorer.
 */

import type { AgentJobsSubfolderNode, AgentJobNode, AgentOperatorNode, AgentAlertNode, AgentProxyNode, DatabaseExplorerNode } from '../../explorer/models/nodeTypes';
import type { ContextMenuAction } from '../../explorer/models/TreeModel';

export interface LoadAgentSubfolderOptions {
  node: AgentJobsSubfolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  onFetchError?: (connectionId: string, errorMessage: string) => void;
  forceReload?: boolean;
}

const JOB_CONTEXT_MENU: ContextMenuAction[] = [
  { id: 'open-job', label: 'Open in Job Scheduler' },
  { id: 'run-job', label: 'Start Job' },
  { id: 'toggle-job', label: 'Enable/Disable' },
];

const OPERATOR_CONTEXT_MENU: ContextMenuAction[] = [
  { id: 'edit-operator', label: 'Edit Operator' },
  { id: 'delete-operator', label: 'Delete Operator' },
];

const ALERT_CONTEXT_MENU: ContextMenuAction[] = [
  { id: 'edit-alert', label: 'Edit Alert' },
  { id: 'toggle-alert', label: 'Enable/Disable' },
  { id: 'delete-alert', label: 'Delete Alert' },
];

const PROXY_CONTEXT_MENU: ContextMenuAction[] = [
  { id: 'edit-proxy', label: 'Edit Proxy' },
  { id: 'delete-proxy', label: 'Delete Proxy' },
];

export async function loadAgentSubfolder(options: LoadAgentSubfolderOptions): Promise<void> {
  const { node, updateNode, onFetchError, forceReload = false } = options;
  const connectionId = node.metadata?.connectionId;
  if (!connectionId) return;

  // Skip if already loaded (unless force reload)
  if (!forceReload && node.children?.length) {
    const hasError = node.children.some((child) => child.type === 'error');
    if (!hasError) return;
  }

  const subfolderName = node.name?.toLowerCase();
  if (!['jobs', 'operators', 'alerts', 'proxies', 'error logs'].includes(subfolderName ?? '')) return;

  const api = window.electronAPI?.jobScheduler;
  if (!api) {
    updateNode(node.id, {
      children: [createAgentErrorNode(node.id, connectionId, 'Job Scheduler API not available')],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, { isLoading: true, children: [] });

  try {
    if (subfolderName === 'jobs') {
      await loadJobChildren(api, node, connectionId, updateNode, onFetchError);
    } else if (subfolderName === 'operators') {
      await loadOperatorChildren(api, node, connectionId, updateNode, onFetchError);
    } else if (subfolderName === 'alerts') {
      await loadAlertChildren(api, node, connectionId, updateNode, onFetchError);
    } else if (subfolderName === 'proxies') {
      await loadProxyChildren(api, node, connectionId, updateNode, onFetchError);
    } else if (subfolderName === 'error logs') {
      loadErrorLogChildren(node, connectionId, updateNode);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    onFetchError?.(connectionId, message);
    updateNode(node.id, {
      children: [createAgentErrorNode(node.id, connectionId, message)],
      isLoading: false,
    });
  }
}

async function loadJobChildren(
  api: NonNullable<typeof window.electronAPI>['jobScheduler'],
  node: AgentJobsSubfolderNode,
  connectionId: string,
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void,
  onFetchError?: (connectionId: string, errorMessage: string) => void,
) {
  const result = await api.listJobs({ connectionId });
  if (!result.success || !result.jobs) {
    const errorMsg = result.error || 'Failed to load jobs';
    onFetchError?.(connectionId, errorMsg);
    updateNode(node.id, { children: [createAgentErrorNode(node.id, connectionId, errorMsg)], isLoading: false });
    return;
  }

  if (result.jobs.length === 0) {
    updateNode(node.id, { children: [{ id: `${node.id}:empty`, parentId: node.id, name: 'No jobs', type: 'message' as const, metadata: { connectionId } }], isLoading: false });
    return;
  }

  const jobNodes: AgentJobNode[] = result.jobs.map((job) => ({
    id: `${node.id}:job-${job.jobId}`, parentId: node.id, name: job.name, type: 'agent-job' as const,
    canHaveChildren: false, children: [], isExpanded: false,
    metadata: { connectionId, jobId: job.jobId, jobName: job.name, enabled: job.enabled, lastRunStatus: job.lastRunStatus },
    contextMenuActions: JOB_CONTEXT_MENU,
  }));
  updateNode(node.id, { children: jobNodes, isLoading: false });
}

async function loadOperatorChildren(
  api: NonNullable<typeof window.electronAPI>['jobScheduler'],
  node: AgentJobsSubfolderNode,
  connectionId: string,
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void,
  onFetchError?: (connectionId: string, errorMessage: string) => void,
) {
  const result = await api.listOperators({ connectionId });
  if (!result.success || !result.operators) {
    const errorMsg = result.error || 'Failed to load operators';
    onFetchError?.(connectionId, errorMsg);
    updateNode(node.id, { children: [createAgentErrorNode(node.id, connectionId, errorMsg)], isLoading: false });
    return;
  }

  if (result.operators.length === 0) {
    updateNode(node.id, { children: [{ id: `${node.id}:empty`, parentId: node.id, name: 'No operators', type: 'message' as const, metadata: { connectionId } }], isLoading: false });
    return;
  }

  const operatorNodes: AgentOperatorNode[] = result.operators.map((op) => ({
    id: `${node.id}:op-${op.id}`, parentId: node.id, name: op.name, type: 'agent-operator' as const,
    canHaveChildren: false, children: [], isExpanded: false,
    metadata: { connectionId, operatorId: op.id, operatorName: op.name, emailAddress: op.emailAddress, enabled: op.enabled },
    contextMenuActions: OPERATOR_CONTEXT_MENU,
  }));
  updateNode(node.id, { children: operatorNodes, isLoading: false });
}

async function loadAlertChildren(
  api: NonNullable<typeof window.electronAPI>['jobScheduler'],
  node: AgentJobsSubfolderNode,
  connectionId: string,
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void,
  onFetchError?: (connectionId: string, errorMessage: string) => void,
) {
  const result = await api.listAlerts({ connectionId });
  if (!result.success || !result.alerts) {
    const errorMsg = result.error || 'Failed to load alerts';
    onFetchError?.(connectionId, errorMsg);
    updateNode(node.id, { children: [createAgentErrorNode(node.id, connectionId, errorMsg)], isLoading: false });
    return;
  }

  if (result.alerts.length === 0) {
    updateNode(node.id, { children: [{ id: `${node.id}:empty`, parentId: node.id, name: 'No alerts', type: 'message' as const, metadata: { connectionId } }], isLoading: false });
    return;
  }

  const alertNodes: AgentAlertNode[] = result.alerts.map((a) => ({
    id: `${node.id}:alert-${a.id}`, parentId: node.id, name: a.name, type: 'agent-alert' as const,
    canHaveChildren: false, children: [], isExpanded: false,
    metadata: { connectionId, alertId: a.id, alertName: a.name, alertType: a.type as 1 | 2 | 3, enabled: a.enabled },
    contextMenuActions: ALERT_CONTEXT_MENU,
  }));
  updateNode(node.id, { children: alertNodes, isLoading: false });
}

function loadErrorLogChildren(
  node: AgentJobsSubfolderNode,
  connectionId: string,
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void,
) {
  const logNodes = [
    { id: `${node.id}:log-0`, parentId: node.id, name: 'Current', type: 'message' as const, metadata: { connectionId } },
    ...Array.from({ length: 9 }, (_, i) => ({
      id: `${node.id}:log-${i + 1}`, parentId: node.id, name: `Archive #${i + 1}`, type: 'message' as const, metadata: { connectionId },
    })),
  ];
  updateNode(node.id, { children: logNodes, isLoading: false });
}

/** Subsystem labels for proxy subfolder display names */
const PROXY_SUBSYSTEM_LABELS: Record<string, string> = {
  CmdExec: 'Operating System (CmdExec)',
  PowerShell: 'PowerShell',
  TSQL: 'Transact-SQL',
  Dts: 'SQL Server Integration Services',
  AnalysisCommand: 'Analysis Services Command',
  AnalysisQuery: 'Analysis Services Query',
  Snapshot: 'Replication Snapshot',
  LogReader: 'Replication Log Reader',
  Distribution: 'Replication Distributor',
  Merge: 'Replication Merge',
  QueueReader: 'Replication Queue Reader',
};

async function loadProxyChildren(
  api: NonNullable<typeof window.electronAPI>['jobScheduler'],
  node: AgentJobsSubfolderNode,
  connectionId: string,
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void,
  onFetchError?: (connectionId: string, errorMessage: string) => void,
) {
  const result = await api.listProxies({ connectionId });
  if (!result.success || !result.proxies) {
    const errorMsg = result.error || 'Failed to load proxies';
    onFetchError?.(connectionId, errorMsg);
    updateNode(node.id, { children: [createAgentErrorNode(node.id, connectionId, errorMsg)], isLoading: false });
    return;
  }

  if (result.proxies.length === 0) {
    updateNode(node.id, { children: [{ id: `${node.id}:empty`, parentId: node.id, name: 'No proxies', type: 'message' as const, metadata: { connectionId } }], isLoading: false });
    return;
  }

  // Group proxies by subsystem into SSMS-style subfolders
  const subfolderChildren = groupProxiesBySubsystem(result.proxies, node.id, connectionId);
  updateNode(node.id, { children: subfolderChildren, isLoading: false });
}

/** Groups proxies by subsystem into subfolder nodes with pre-populated children */
export function groupProxiesBySubsystem(
  proxies: Array<{ proxyId: number; name: string; enabled: boolean; credentialName?: string | null; subsystems?: Array<{ subsystemId: number; subsystemName: string }> }>,
  parentId: string,
  connectionId: string,
): DatabaseExplorerNode[] {
  // Collect subsystems that have at least one proxy
  const subsystemMap = new Map<string, typeof proxies>();
  for (const p of proxies) {
    if (!p.subsystems?.length) continue;
    for (const sub of p.subsystems) {
      const key = sub.subsystemName;
      if (!subsystemMap.has(key)) subsystemMap.set(key, []);
      subsystemMap.get(key)!.push(p);
    }
  }

  // Proxies with no subsystems go into an "Unassigned" folder
  const unassigned = proxies.filter((p) => !p.subsystems?.length);

  const folders: DatabaseExplorerNode[] = [];

  // Create a subfolder for each subsystem (sorted by label)
  const sortedKeys = [...subsystemMap.keys()].sort((a, b) => {
    const la = PROXY_SUBSYSTEM_LABELS[a] ?? a;
    const lb = PROXY_SUBSYSTEM_LABELS[b] ?? b;
    return la.localeCompare(lb);
  });

  for (const subsystemName of sortedKeys) {
    const subProxies = subsystemMap.get(subsystemName)!;
    const folderId = `${parentId}:subsystem-${subsystemName}`;
    const label = PROXY_SUBSYSTEM_LABELS[subsystemName] ?? subsystemName;

    const proxyNodes: AgentProxyNode[] = subProxies.map((p) => ({
      id: `${folderId}:proxy-${p.proxyId}`, parentId: folderId, name: p.name, type: 'agent-proxy' as const,
      canHaveChildren: false, children: [], isExpanded: false,
      metadata: { connectionId, proxyId: p.proxyId, proxyName: p.name, credentialName: p.credentialName ?? null, enabled: p.enabled },
      contextMenuActions: PROXY_CONTEXT_MENU,
    }));

    folders.push({
      id: folderId, parentId, name: label, type: 'agent-jobs-subfolder' as const,
      canHaveChildren: true, children: proxyNodes, isExpanded: false,
      metadata: { connectionId },
    } as AgentJobsSubfolderNode);
  }

  // Add unassigned proxies directly (no subfolder)
  for (const p of unassigned) {
    folders.push({
      id: `${parentId}:proxy-${p.proxyId}`, parentId, name: p.name, type: 'agent-proxy' as const,
      canHaveChildren: false, children: [], isExpanded: false,
      metadata: { connectionId, proxyId: p.proxyId, proxyName: p.name, credentialName: p.credentialName ?? null, enabled: p.enabled },
      contextMenuActions: PROXY_CONTEXT_MENU,
    } as AgentProxyNode);
  }

  return folders;
}

function createAgentErrorNode(parentId: string, connectionId: string, errorMessage: string) {
  return {
    id: `${parentId}:error`,
    parentId,
    name: errorMessage,
    type: 'error' as const,
    metadata: { connectionId, error: errorMessage },
  };
}
